import { useLocalization } from '@/data/Localization';
import { useNavigation } from '@/data/Navigation';
import { useSettings } from '@/data/Settings';
import React, { FC, Fragment, useMemo } from 'react';
import { useContentRecommendation } from '@/data/Content/ContentRecommendation';
import { WidgetProperties } from '@/data/types/Slot';
import { RenderContent } from '@/components/blocks/RenderContent';

const categoryLinksSEOId = 'CategoryLinksSEO';

const cleanHtmlAttributes = (html: string): string => {
	// Process anchor tags - remove inner HTML tags, keep the content
	let result = html.replace(
		/<a([^>]*)>([\s\S]*?)<\/a>/g,
		(_, attributes, content) => {
			const textContent = content.replace(/<[^>]*>/g, '').trim();
			return `<a${attributes}>${textContent}</a>`;
		}
	);

	// Clean class and id attributes from all HTML elements
	result = result.replace(
		/<([a-z][a-z0-9]*)([^>]*)>/gi,
		(_, tagName, attributes) => {
			const cleanedAttributes = attributes
				.replace(/\s+(class|id)\s*=\s*(["'])[^"']*\2|\s+(class|id)\s*=[^\s>]*/gi, '');
			return `<${tagName}${cleanedAttributes}>`;
		}
	);

	// Remove newlines, trim whitespace
	return result.replace(/\n/g, ' ').replace(/\s+/g, ' ');
};

const CategoryLinkContentRecommendation = ({
	properties = {},
}: {
	properties: WidgetProperties;
}) => {
	const { emsName = '' } = properties;
	const { data, title } = useContentRecommendation(emsName);

	const items = useMemo(
		() =>
			[...(title ?? []), ...(data ?? [])]
				.filter(({ text }) => !!text)
				.map((item) => ({
					...item,
					text: cleanHtmlAttributes(item.text as string),
				})),
		// Reason: should never change, should only render once
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	if (!items.length) return null;

	return items.length === 1 ? (
		<li>
			<RenderContent content={items[0]} />
		</li>
	) : (
		<li>
			<ul>
				{items.map((item, index) => (
					<li key={index}>
						<RenderContent content={item} />
					</li>
				))}
			</ul>
		</li>
	);
};

const CategoryLinksSEORenderer: FC = () => {
	const { settings } = useSettings();
	const { navigation } = useNavigation();
	const { AllCategoriesLabel } = useLocalization('AllCategoriesExpandedMenu');
	const basePath = settings.userData.SiteLink_DomainName;
	const filteredNavigation = useMemo(
		() =>
			navigation
				? navigation.filter((pageLink) => pageLink.label !== AllCategoriesLabel.t())
				: undefined,
		[AllCategoriesLabel, navigation]
	);
	return (
		<ul style={{ display: 'none' }} className="CategoryLinksSEO" id={categoryLinksSEOId}>
			{filteredNavigation?.map((cat, i) => (
				<Fragment key={i}>
					<CategoryLinkContentRecommendation
						properties={{ emsName: `${cat?.identifier}_Sub_Category_Top_Text_eSpot_rv` }}
					/>
					<li>
						<a href={`${basePath}${cat?.url}`}>{cat.label}</a>
						{cat?.children?.length ? (
							<ul>
								{cat?.children?.map((cat1, i1) => (
									<li key={i1}>
										<a href={`${basePath}${cat1?.url}`}>{cat1.label}</a>
										{cat1?.children?.length ? (
											<ul>
												{cat1?.children?.map((cat2, i2) => (
													<li key={i2}>
														<a href={`${basePath}${cat2?.url}`}>{cat2.label}</a>
													</li>
												))}
											</ul>
										) : null}
									</li>
								))}
							</ul>
						) : null}
					</li>
					<CategoryLinkContentRecommendation
						properties={{ emsName: `${cat?.identifier}_Category_Brand_List_eSpot` }}
					/>
					<CategoryLinkContentRecommendation
						properties={{
							emsName: `${cat?.identifier}_Category_Site_Wide_Promotional_Bar_espot_rv`,
						}}
					/>
				</Fragment>
			))}
		</ul>
	);
};

export const CategoryLinksSEO: FC = () => {
	const Component = useMemo(() => {
		if (typeof window === 'undefined') return CategoryLinksSEORenderer;

		// Prevents re-rendering on client side, as the content is already rendered on server side and is non-interactive
		const categoryLinksSEO = (window.categoryLinksSEO =
			window.categoryLinksSEO || document.getElementById(categoryLinksSEOId)?.innerHTML);
		if (categoryLinksSEO) {
			return () => (
				<ul
					style={{ display: 'none' }}
					className="CategoryLinksSEO"
					id={categoryLinksSEOId}
					dangerouslySetInnerHTML={{ __html: categoryLinksSEO }}
				></ul>
			);
		}

		return CategoryLinksSEORenderer;
	}, []);

	return <Component />;
};
